import { Card, CardContent, CardHeader } from '@frontend/shadcn/components/ui/card';
import { Button } from '@frontend/shadcn/components/ui/button';
import { PaletteIcon, ArrowLeftIcon, PenIcon } from '@revfluence/fresh-icons/solid/esm';
import React, { useEffect, useMemo, useState } from 'react';
import { Switcher } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { useHistory, useParams } from 'react-router-dom';
import { ProgramOnboardingTemplateType } from '@frontend/app/types/globalTypes';
import { useAuth } from '@frontend/context/authContext';
import { useGetClientLogoQuery } from '@frontend/app/hooks/useGetClientLogoQuery';
import { useSelectedPageType, useRecruitment } from './RecruitmentContext';
import { PulseHeroPage } from './Components/PulseHeroPage';
import { ThemingPage } from './ThemingPage';
import { TemplatePreview } from './TemplatePreview';
import { useGetProgramOnboardingTemplateById } from './hooks/useGetProgramOnboardingTemplateById';
import { AmplifyHomePage } from './Components/AmplifyHomePage';
import { useFields } from '../../LandingPages/LeftPanel/hooks';
import { ConfirmationPage } from './Components/ConfirmationPage';
import { BriefPage } from './Components/BriefPage';
import { SettingsRoutes } from '../../constants';
import { FormEditor } from './Components/FormEditor';
import { ProductPage } from './Components/ProductPage';

export const TemplateEditor = () => {
  const { initPulseTemplate, initAmplifyTemplate } = useRecruitment();
  const history = useHistory();
  const { projectId, templateId } = useParams<{ projectId: string; templateId: string }>();

  const { data: template, loading: templateLoading } = useGetProgramOnboardingTemplateById({
    variables: {
      id: Number(templateId),
    },
  });

  const formFields = useMemo(() => {
    if (template?.template?.config.amplifyTemplate) {
      const pages = template.template.config.amplifyTemplate.pages;
      const formFields = pages.map((page) => page?.home?.forms?.applicationFormFields).filter(Boolean);
      return formFields[0] || null;
    } else if (template?.template?.config.pulseTemplate) {
      const pages = template.template.config.pulseTemplate.pages;
      const formFields = pages.map((page) => page?.hero?.applicationFormFields).filter(Boolean);
      return formFields[0] || null;
    }
    return null;
  }, [template]);

  const extendedFormFields = useMemo(() => {
    if (template?.template?.config.amplifyTemplate) {
      const pages = template.template.config.amplifyTemplate.pages;
      const formFields = pages.map((page) => page?.forms?.applicationFormFields).filter(Boolean);
      return formFields[0] || null;
    } else if (template?.template?.config.pulseTemplate) {
      const pages = template.template.config.pulseTemplate.pages;
      const formFields = pages.map((page) => page?.forms?.applicationFormFields).filter(Boolean);
      return formFields[0] || null;
    }
    return null;
  }, [template]);

  const savedFields = useFields(formFields);
  const extendedSavedFields = useFields(extendedFormFields);

  const { clientInfo } = useAuth();

  const { logoUrl } = useGetClientLogoQuery();

  useEffect(() => {
    if (template?.template) {
      if (template.template.templateType === ProgramOnboardingTemplateType.PULSE) {
        initPulseTemplate({
          ...template.template,
          brandLogo: logoUrl,
          brandName: clientInfo?.name,
        }, savedFields, extendedSavedFields);
      } else {
        initAmplifyTemplate({
          ...template.template,
          brandLogo: logoUrl,
          brandName: clientInfo?.name,
        }, savedFields, extendedSavedFields);
      }
    }
  }, [initPulseTemplate, initAmplifyTemplate, template, savedFields, extendedSavedFields, clientInfo?.name, logoUrl]);

  const onClickBack = () => {
    history.push(SettingsRoutes.recruiting.index.replace(':projectId', projectId));
  };

  const selectedPageType = useSelectedPageType();

  const [selectedTab, setSelectedTab] = useState<'sections' | 'theming'>('sections');

  if (templateLoading) {
    return (
      <div className="p-6 flex gap-6">
        <Skeleton className="w-[322px] h-[calc(100vh-80px-38px-24px-65px-28px)] rounded-2xl" />
        <Skeleton className="flex-1 h-[calc(100vh-80px-38px-24px-65px-28px)] rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="p-6 flex gap-6">
      <Card className="w-[322px] rounded-2xl">
        <CardHeader className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="headerIcon" onClick={onClickBack}>
                <ArrowLeftIcon />
              </Button>
              <Switcher
                items={[
                  { key: 'sections', icon: <PenIcon fontSize={12} />, tooltip: 'Customize Application' },
                  { key: 'theming', icon: <PaletteIcon fontSize={12} />, tooltip: 'Themes' },
                ]}
                type="single"
                activeKeys={[selectedTab]}
                onChange={(value) => setSelectedTab(value[0] as 'sections' | 'theming')}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-6 h-[calc(100vh-80px-38px-24px-65px-28px)] overflow-y-auto">
          {selectedTab === 'sections' && selectedPageType === 'hero' && <PulseHeroPage />}
          {selectedTab === 'sections' && selectedPageType === 'home' && <AmplifyHomePage />}
          {selectedTab === 'sections' && selectedPageType === 'confirmation' && <ConfirmationPage />}
          {selectedTab === 'sections' && selectedPageType === 'brief' && <BriefPage />}
          {selectedTab === 'sections' && selectedPageType === 'form' && <FormEditor />}
          {selectedTab === 'sections' && selectedPageType === 'product' && <ProductPage />}
          {selectedTab === 'theming' && <ThemingPage />}
        </CardContent>
      </Card>
      <div className="flex-1">
        <TemplatePreview />
      </div>
    </div>
  );
};
